import { SyntheticEvent, useEffect, useState } from "react";

import Briefcase01 from "@uui/Briefcase01";
import Building07 from "@uui/Building07";
import LinkExternal01 from "@uui/LinkExternal01";
import User01 from "@uui/User01";
import { isEmpty, partition, sortBy } from "lodash/fp";

import {
  Box,
  FormControlLabel,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";

import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useRollupContext from "@app/analytics/hooks/meetops/useRollupContext";
import {
  ALL_TEAMS,
  ALL_USERS,
  EMPTY_FILTER,
  RollupFilterOption,
} from "@app/analytics/providers/RollupContext";
import theme from "@app/analytics/theme";
import useAuth from "@app/common/auth/useAuth";
import Link from "@app/common/components/Link";
import Switch from "@app/common/components/Switch";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import { showToast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";
import { User } from "@app/common/types";
import { planIsUnknown } from "@app/common/utils/billingUtils";
import {
  EnterpriseUser,
  EnterpriseWorkspace,
  EnterpriseWorkspaceTeam,
} from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import { DesktopOnlyRow } from "@rui/Stack";

import FilterDropdown from "./FilterDropdown";
import RollupFilterIcon from "./RollupFilterIcon";

const LOADING = { id: "LOADING", label: "Loading..." };

export const getTeamFilterOptions = (
  canAccessWorkspaceRollups: boolean,
  workspaceTeams: EnterpriseWorkspaceTeam[]
): RollupFilterOption[] => {
  const teamFilters = sortBy(
    "label",
    workspaceTeams.map((t) => ({ id: t.id, label: t.name }))
  );

  if (canAccessWorkspaceRollups) {
    teamFilters.unshift(ALL_TEAMS);
  }
  return teamFilters;
};

const getOptionsFromUsers = ({
  currentUser,
  users,
  canAccessOtherUserData,
  includeAllUsersOption,
}: {
  currentUser: User;
  users: EnterpriseUser[];
  canAccessOtherUserData: boolean;
  includeAllUsersOption: boolean;
}): RollupFilterOption[] => {
  const userFilters = canAccessOtherUserData
    ? sortBy(
        "label",
        users.map((u) => ({
          id: u.userId,
          label: u.fullName,
        }))
      )
    : [{ id: currentUser.id, label: currentUser.fullName }];

  if (includeAllUsersOption) {
    userFilters.unshift(ALL_USERS);
  }

  return userFilters;
};

const getUserFilterOptions = (
  canAccessTeamRollups: boolean,
  canAccessWorkspaceRollups: boolean,
  teamFilterId: string,
  workspace: EnterpriseWorkspace,
  canAccessTeamDataAtWorkspaceLevel: boolean,
  currentUser: User | null,
  globalSessionAccessEnabled?: boolean
) => {
  if (!currentUser) return [];

  if (teamFilterId === EMPTY_FILTER.id) {
    // if the user is not on or does not have access to any teams,
    // they can only view their own data
    return [{ id: currentUser.id, label: currentUser.fullName }];
  }

  if (teamFilterId === ALL_TEAMS.id) {
    return getOptionsFromUsers({
      currentUser,
      users: workspace.users,
      canAccessOtherUserData:
        canAccessTeamDataAtWorkspaceLevel &&
        Boolean(globalSessionAccessEnabled),
      includeAllUsersOption:
        canAccessWorkspaceRollups && Boolean(globalSessionAccessEnabled),
    });
  }

  const selectedTeam = workspace.teams.find((team) => team.id === teamFilterId);
  const canViewTeamDataForSelectedTeam =
    currentUser?.workspace?.teams.find((team) => team.id === teamFilterId)
      ?.canViewTeamData ?? false;

  return getOptionsFromUsers({
    currentUser,
    users: selectedTeam?.users ?? [],
    canAccessOtherUserData: canAccessTeamDataAtWorkspaceLevel,
    includeAllUsersOption: Boolean(
      canAccessTeamRollups && canViewTeamDataForSelectedTeam
    ),
  });
};

const isJustMeDisabled = (
  user: User | null,
  canAccessWorkspaceRollups: boolean,
  userOptions: RollupFilterOption[]
) => {
  if (user == null) return true;
  if (canAccessWorkspaceRollups) return false;

  const [meOption, otherUserOptions] = partition(
    (opt) => opt.id === user.id,
    userOptions
  );
  if (isEmpty(meOption)) return true;

  return isEmpty(otherUserOptions);
};

const RollupFilter = () => {
  const { user } = useAuth();
  const isDesktopView = useMediaQuery(theme.breakpoints.up("sm"));

  const { teamFilter, setTeamFilter, usersFilter, setUsersFilter } =
    useRollupContext();
  const { currentPlan } = useBillingContext();

  const {
    canAccessTeamMemberData: canAccessTeamMemberDataAtWorkspaceLevel,
    canAccessTeamRollupReports,
    canAccessWorkspaceRollupReports,
  } = useFeatureAccess();

  const { inWorkspace, workspace, workspaceLoading, workspaceUser } =
    useWorkspace();

  const [onlyMe, setOnlyMe] = useState(true);

  const teamsFilterOptions = getTeamFilterOptions(
    canAccessWorkspaceRollupReports,
    workspace.teams
  );

  const usersFilterOptions = getUserFilterOptions(
    canAccessTeamRollupReports,
    canAccessWorkspaceRollupReports,
    teamFilter.id,
    workspace,
    canAccessTeamMemberDataAtWorkspaceLevel,
    user,
    workspaceUser.globalSessionAccessEnabled
  );

  useEffect(() => {
    if (teamFilter.id === EMPTY_FILTER.id && !isEmpty(teamsFilterOptions)) {
      setTeamFilter(teamsFilterOptions[0]);
    } else if (teamFilter.id !== EMPTY_FILTER.id && !workspaceLoading) {
      if (
        teamFilter.id === ALL_TEAMS.id &&
        !workspaceUser.globalSessionAccessEnabled
      ) {
        if (user && usersFilter.id !== user.id) {
          showToast({
            severity: "info",
            message: `You do not have permission to view all team reports for ${usersFilter.label}. Read is now showing just your meetings.`,
          });
        }
        if (user) {
          // set user filter to current user if the currently selected user will result in a 400
          setUsersFilter({ id: user.id, label: user.fullName });
        }
      }
      const currentSelectedUserInNewTeam = usersFilterOptions.find(
        (opt) => opt.id === usersFilter.id
      );

      const newUserFilter = usersFilterOptions[0];
      if (!currentSelectedUserInNewTeam && newUserFilter) {
        setUsersFilter(newUserFilter);
      }
    }
  }, [
    teamFilter.id,
    canAccessWorkspaceRollupReports,
    canAccessTeamRollupReports,
    usersFilterOptions.length,
    workspaceLoading,
  ]);

  useEffect(() => {
    if (!user) return;
    setOnlyMe(usersFilter.id === user.id);
  }, [usersFilter.id]);

  if (!user || !inWorkspace || planIsUnknown(currentPlan.type)) return null;

  const handleToggleOnlyMe = (
    e: SyntheticEvent<Element, Event>,
    checked: boolean
  ) => {
    if (!user) return;

    if (checked) {
      const notOnSelectedTeam = isEmpty(
        usersFilterOptions.filter((f) => f.id === user.id)
      );

      if (canAccessWorkspaceRollupReports && notOnSelectedTeam) {
        setTeamFilter(ALL_TEAMS);
      }

      setUsersFilter({ id: user.id, label: user.fullName });
    } else {
      if (canAccessTeamRollupReports) {
        setUsersFilter(ALL_USERS);
      } else {
        const newUserFilter = usersFilterOptions.find((f) => f.id !== user.id);
        if (newUserFilter) {
          setUsersFilter(newUserFilter);
        }
      }
    }
  };

  const justMeToggleDisabled = isJustMeDisabled(
    user,
    canAccessWorkspaceRollupReports,
    usersFilterOptions
  );

  const showTeamsFilter = !isEmpty(workspaceUser.teams);
  const showUsersFilter = usersFilterOptions.length > 1;

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent={{ xxs: "center", sm: "space-between" }}
      sx={{
        width: "100%",
        bgcolor: "shades.ghost.300",
        py: { xxs: 1.5, sm: 2 },
        px: { xxs: 2, sm: 3 },
        overflowX: "auto",
        position: { xxs: "fixed", sm: "relative" },
        bottom: 0,
        zIndex: { xxs: 10, sm: 0 },
      }}
    >
      <Stack direction="row" spacing={2}>
        <DesktopOnlyRow alignItems="center" sx={{ mx: 2 }}>
          <RollupFilterIcon icon={Building07} />
          <Typography variant="bodySecondaryMed">
            {workspace.name || "Loading..."}
          </Typography>
        </DesktopOnlyRow>
        {showTeamsFilter && (
          <FilterDropdown
            id="teams-filter"
            options={teamsFilterOptions}
            selected={workspaceLoading || !teamFilter ? LOADING : teamFilter}
            onChange={setTeamFilter}
            icon={Briefcase01}
            small={!isDesktopView}
          />
        )}
        {showUsersFilter && (
          <>
            <FilterDropdown
              id="users-filter"
              options={usersFilterOptions}
              selected={
                workspaceLoading || !usersFilter ? LOADING : usersFilter
              }
              onChange={setUsersFilter}
              icon={User01}
              small={!isDesktopView}
            />
            <FormControlLabel
              checked={onlyMe}
              control={<Switch disabled={justMeToggleDisabled} />}
              onChange={handleToggleOnlyMe}
              label={
                <Typography
                  variant="bodySecondaryMed"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Just me
                </Typography>
              }
            />
          </>
        )}
      </Stack>
      <Link
        variant="bodySecondaryMed"
        href={LINKS.MARKETING.WORKSPACES}
        sx={{
          fontWeight: 500,
          display: { sm: "none", lg: "flex" },
          ml: 3,
        }}
      >
        <DesktopOnlyRow spacing={0.5} sx={{ whiteSpace: "nowrap" }}>
          <Box component="span">Learn more about Workspaces </Box>
          <LinkExternal01 sx={{ ml: 1, height: "16px", width: "16px" }} />
        </DesktopOnlyRow>
      </Link>
    </Stack>
  );
};

export default RollupFilter;
